import React, { useState, useCallback, memo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import { getIn, unwrap } from '../../utils/ChangeSpy';
import {
  createOneOff,
  changeField,
  revertUpdate,
  findDirectors,
  findAnalysts,
  findBuyers,
  findProjects,
  getCountTargets,
} from '../../actions/oneOff';
import AutoCompleteContainer from '../AutoComplete';
import { dateToString } from '../../utils/dateFormat';
import { showError, showInformationCustomTitle } from '../../utils/MessagePopup';
import { usePopupContext } from '../../contexts/PopupContext';

const OneOffApproachPopupContainer = props => {
  const { popup, oneOff, loadingCountTargets, isValid, countTargets, onErrorClose } = props;
  const { openPopup, closePopup } = usePopupContext();
  const [loading, setLoading] = useState(false);

  const handleChange = useCallback(
    event => {
      const { name, value: targetValue, checked, type } = event.target;
      const value = type === 'checkbox' ? checked : targetValue;

      return props.changeField({ name, value });
    },
    [props.changeField],
  );

  const handleClose = useCallback(() => {
    props.revertUpdate();
    closePopup();
  }, [props.revertUpdate, closePopup]);

  const handleSave = useCallback(() => {
    const callback = popup.get('props').get('callback');

    setLoading(true);

    const body = {
      tag: getIn(oneOff, 'tag'),
      buyerId: getIn(oneOff, 'buyerId'),
      projectId: getIn(oneOff, 'projectId'),
      recordOwnerId: getIn(oneOff, 'recordOwnerId'),
      recordSubOwnerId: getIn(oneOff, 'recordSubOwnerId'),
      description: getIn(oneOff, 'description'),
      date: dateToString(popup.get('props').get('dateQuery')),
    };

    props.createOneOff({
      body,
      afterSuccess: ({ response: { list } }) => {
        setLoading(false);
        handleClose();
        callback();

        const header = `"One-Off" mailing was not created for ${list.quantitySkipped} companies because of:`;
        const popupMessages = [];

        if (list.skipRF.length) {
          const missingTitle = 'The following profiles were missing information: \n';
          let skipMessage = '';

          list.skipRF.forEach(error => {
            let errors = '';
            const link = `( ${window.location.origin}/company/${error.companyId}/target )`;
            const companyName = `${error.companyName} ${link}\n`;

            error.emptyRequiredFields.forEach(message => {
              errors += `- ${message}.\n`;
            });
            skipMessage = `${skipMessage} ${companyName} ${errors}`;
          });
          popupMessages.push(`${missingTitle} ${skipMessage}`);
        }

        if (list.skipE.length) {
          const missingTitle = 'The following companies have been skipped because: \n';
          let skipMessage = '';

          list.skipE.forEach(error => {
            const link = `( ${window.location.origin}/company/${error.companyId}/target )`;
            const errorLink = `${error.error} ${link}\n`;

            skipMessage = `${skipMessage} ${errorLink}`;
          });
          popupMessages.push(`${missingTitle} ${skipMessage}`);
        }

        if (list.skipM.length) {
          const missingTitle = 'The following companies have been skipped because: \n';
          let skipMessage = '';

          list.skipM.forEach(error => {
            const link = `( ${window.location.origin}/company/${error.companyId}/target )`;
            const errorLink = `${error.error} ${link}\n`;

            skipMessage = `${skipMessage} ${errorLink}`;
          });
          popupMessages.push(`${missingTitle} ${skipMessage}`);
        }

        if (list.skipHOO.length) {
          const missingTitle = 'The following companies have been skipped because: \n';
          let skipMessage = '';

          list.skipHOO.forEach(error => {
            const link = `( ${window.location.origin}/company/${error.companyId}/target )`;
            const errorLink = `${error.error} ${link}\n`;

            skipMessage = `${skipMessage} ${errorLink}`;
          });
          popupMessages.push(`${missingTitle} ${skipMessage}`);
        }

        showInformationCustomTitle(openPopup, header, popupMessages, () => {
          closePopup();
        });
      },
      afterError: ({ responseBody }) => {
        setLoading(false);
        showError(
          openPopup,
          responseBody?.errors
            ? Object.entries(responseBody.errors).map(value => value[1].toString())
            : [responseBody?.message || 'An error occurred'],
        );
      },
    });
  }, [popup, oneOff, props.createOneOff, handleClose, openPopup, closePopup]);

  const handleUserSuggestChange = useCallback(({ name, value }) => props.changeField({ name, value }), [
    props.changeField,
  ]);

  const handleCountTarget = useCallback(() => {
    const tag = unwrap(oneOff.getIn(['tag']));

    props.getCountTargets(tag);
  }, [oneOff, props.getCountTargets]);

  const buyerId = unwrap(oneOff.getIn(['buyerId']));

  const suggestModule = (
    <AutoCompleteContainer
      idName="recordOwnerId"
      inputProps={{
        name: 'recordOwner',
        className: 'form-control',
        label: 'Module',
      }}
      name="recordOwner"
      onChange={handleUserSuggestChange}
      onFind={options => {
        props.findDirectors({
          ...options,
          field: 'recordOwner',
        });
      }}
      rootPath={['oneOff']}
      suggestionsName="suggestModule"
      valueName="recordOwner"
    />
  );

  const suggestAnalyst = (
    <AutoCompleteContainer
      idName="recordSubOwnerId"
      inputProps={{
        name: 'recordSubOwner',
        className: 'form-control',
        label: 'A/A',
      }}
      name="recordSubOwner"
      onChange={handleUserSuggestChange}
      onFind={options => {
        props.findAnalysts({
          ...options,
          field: 'recordSubOwner',
        });
      }}
      rootPath={['oneOff']}
      suggestionsName="suggestAnalyst"
      valueName="recordSubOwner"
    />
  );

  const suggestBuyer = (
    <AutoCompleteContainer
      idName="buyerId"
      inputProps={{
        name: 'buyer',
        className: 'form-control',
        label: 'Buyer',
      }}
      name="buyer"
      onChange={handleUserSuggestChange}
      onFind={options => {
        props.findBuyers({
          ...options,
          field: 'buyer',
        });
      }}
      rootPath={['oneOff']}
      suggestionsName="suggestBuyer"
      valueName="buyer"
    />
  );

  const suggestProject = (
    <AutoCompleteContainer
      idName="projectId"
      inputProps={{
        name: 'project',
        className: 'form-control',
        label: 'Project',
        disabled: !buyerId,
      }}
      name="project"
      onChange={handleUserSuggestChange}
      onFind={options => {
        props.findProjects({
          ...options,
          field: 'project',
          buyerId,
        });
      }}
      rootPath={['oneOff']}
      suggestionsName="suggestProject"
      valueName="project"
      suggestIfEmpty
    />
  );

  return (
    <Popups.OneOffApproachPopup
      countTargets={countTargets}
      isValid={isValid}
      loading={loading}
      loadingCountTargets={loadingCountTargets}
      onChange={handleChange}
      onClose={handleClose}
      onCountTargets={handleCountTarget}
      oneOff={oneOff}
      onErrorClose={onErrorClose}
      onSave={handleSave}
      suggestAnalyst={suggestAnalyst}
      suggestBuyer={suggestBuyer}
      suggestModule={suggestModule}
      suggestProject={suggestProject}
    />
  );
};

function mapStateToProps(state, props) {
  return {
    ...props,
    oneOff: state.oneOff,
    loadingCountTargets: state.oneOff.get('loadingCountTargets'),
    countTargets: state.oneOff.get('countTargets'),
    isValid: state.oneOff.get('isValid', false),
    inputErrors: state.oneOff.get('inputErrors'),
  };
}

const OneOffApproachPopupContainerMemo = memo(OneOffApproachPopupContainer);

export { OneOffApproachPopupContainer };
export default connect(
  mapStateToProps,
  {
    createOneOff,
    changeField,
    revertUpdate,
    findDirectors,
    findAnalysts,
    findBuyers,
    findProjects,
    getCountTargets,
  },
  mergeProps,
  connectOptions,
)(withRouter(OneOffApproachPopupContainerMemo));
